import { mix, getLuminance } from 'polished';
import { createGlobalStyle, css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

import { createHighlightStyles } from '../hooks/useHighlight';
import { defaultThemeProp } from '../theme';

import { calculateFontSize } from './utils';
import type { FontSize } from './utils';

export const globalSpacingStyles = css`
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
`;

export const createGlobalRootStyles = ({
  base: { scale, 'hit-area': hitArea, palette }
}: DefaultTheme) => {
  return css`
    :root,
    :host {
      height: 100%;
      -webkit-text-size-adjust: 100%;
      -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
      font-size: calc(${scale} * 1rem);
      --hit-area: ${hitArea.compact};
      /* stylelint-disable unit-allowed-list, length-zero-no-unit */
      --safe-area-inset-top: env(safe-area-inset-top, 0px);
      --safe-area-inset-bottom: env(safe-area-inset-bottom, 0px);
      &:dir(ltr) {
        --safe-area-inset-inline-start: env(safe-area-inset-left, 0px);
        --safe-area-inset-inline-end: env(safe-area-inset-right, 0px);
      }

      &:dir(rtl) {
        --safe-area-inset-inline-start: env(safe-area-inset-right, 0px);
        --safe-area-inset-inline-end: env(safe-area-inset-left, 0px);
      }
      /* stylelint-enable unit-allowed-list, length-zero-no-unit */
      scroll-padding-block-start: calc(4rem + var(--appshell-top-offset, 0rem));

      @media (pointer: coarse) {
        --hit-area: ${hitArea.finger};
      }
      color-scheme: ${getLuminance(palette['foreground-color']) > 0.5 ? 'dark' : 'light'};
    }
  `.join('');
};

export const createGlobalBodyStyles = ({
  base: {
    'font-size': baseFontSize,
    'font-scale': baseFontScale,
    'font-stretch': baseFontStretch,
    'letter-spacing': baseLetterSpacing,
    'font-family': fontFamily,
    palette: {
      'foreground-color': foregroundColor,
      'app-background': background,
      'primary-background': primaryBackground
    },
    'line-height': lineHeight
  },
  components: { text }
}: DefaultTheme) => {
  // Can't call a hook here
  const fontSize = calculateFontSize(baseFontSize, baseFontScale);

  return css`
    body,
    :host {
      min-height: 100%;
      margin: 0;
      padding: 0;
      font-size: ${fontSize[text.primary['font-size'] as FontSize]};
      font-family: ${fontFamily};
      color: ${foregroundColor};
      line-height: ${lineHeight};
      font-stretch: ${baseFontStretch};
      letter-spacing: ${baseLetterSpacing};
    }

    body {
      background: ${background};
      background-color: ${primaryBackground};
    }
  `.join('');
};

export default createGlobalStyle(({ theme = defaultThemeProp.theme }) => {
  const {
    base: {
      'custom-scrollbar': customScrollbar,
      palette: { 'foreground-color': foreground },
      shadow: { focus },
      transparency: { 'transparent-3': transparent }
    },
    components: {
      'form-control': { 'background-color': backgroundColor }
    }
  } = theme;
  return css`
    ${globalSpacingStyles}

    body {
      overscroll-behavior: none;
    }

    h1:focus-visible,
    h2:focus-visible,
    h3:focus-visible,
    h4:focus-visible,
    h5:focus-visible,
    h6:focus-visible {
      @media (forced-colors: none) {
        outline: transparent;
      }
      box-shadow: ${focus};
    }

    ::placeholder {
      color: ${mix(transparent, foreground, backgroundColor)};
      opacity: unset;
    }

    ${customScrollbar &&
    css`
      * {
        scrollbar-width: thin;
      }
    `}

    ${createGlobalRootStyles(theme)}

    ${createGlobalBodyStyles(theme)}

    ${createHighlightStyles(theme)}

    input,
    button,
    select,
    optgroup,
    textarea {
      margin: 0;
      font-family: inherit;
      font-size: inherit;
      line-height: inherit;
    }
  `;
});
